import { useRef, useState, type ReactNode, type Ref, type RefObject } from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@nucleus-commerce/ui/components/popover';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { segmentDisplay, type DateOrder, type TemporalLocale } from './locale';
import {
  composeValue,
  stepSegment,
  typeDigit,
  typingLimits,
  type SegmentOptions,
  type SegmentType,
  type SegmentValues,
  type TemporalKind,
} from './segments';
import { nowIn } from './temporal';
import { useLabelFor } from './use-label-for';
import { useSegmentState } from './use-segment-state';
import { useTemporalLocale } from './use-temporal-locale';

/**
 * Props every picker shares. They mirror `<input type="date|time|datetime-local">`, so
 * `<Picker {...field} />` replaces `<Input type=… {...field} />` inside a `FormControl`.
 */
export interface TemporalInputProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'onBlur' | 'defaultValue' | 'children' | 'dir'
> {
  /** The native value string; `''` for none. A partially typed value reports `''`. */
  value?: string | null;
  defaultValue?: string;
  /** Receives the new value string; react-hook-form's `field.onChange` fits. */
  onChange?: (value: string) => void;
  /** Focus left the field and its picker. */
  onBlur?: () => void;
  /** Submitted with a surrounding `<form>` through a hidden input. */
  name?: string;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  /** Same format as the value; the picker disables what lies outside. */
  min?: string;
  max?: string;
  /** Focuses the first segment (react-hook-form's `setFocus` and focus on error). */
  ref?: Ref<HTMLElement>;
  /** A BCP-47 tag overriding the app's formatting locale (segment order, clock, week start). */
  locale?: string;
  /** Date segment order; day before month unless `locale` asks for the locale's own order. */
  dateOrder?: DateOrder;
  /**
   * The IANA zone whose clock "Today", "Now" and the today mark follow (the value's own zone);
   * the browser's when omitted. Values are never converted.
   */
  timeZone?: string;
}

export interface TemporalPanelApi {
  segments: SegmentValues;
  options: SegmentOptions;
  locale: TemporalLocale;
  /** Sets the segments and reports the value, as typing does. */
  update: (next: SegmentValues) => void;
  close: () => void;
  /** The panel's Escape handler: return true when it handled the key (e.g. left a sub-view). */
  escapeRef: RefObject<(() => boolean) | null>;
  /** Called when the popup opens, to focus the panel's starting element. */
  initialFocusRef: RefObject<(() => void) | null>;
  timeZone: string | undefined;
}

interface TemporalFieldProps extends TemporalInputProps {
  kind: TemporalKind;
  minuteStep: number;
  triggerIcon: ReactNode;
  /** The icon button's and the popup's accessible name ("Choose date"). */
  triggerLabel: string;
  contentClassName?: string;
  renderPanel: (api: TemporalPanelApi) => ReactNode;
}

const SEPARATORS = '/-.:, ';

/**
 * A native-like implicit submission: Enter presses the form's default button, the first submit
 * button the form owns (`form.elements` includes buttons tied to it by a `form` attribute).
 */
function submitImplicitly(from: HTMLElement) {
  const form = from.closest('form');
  if (!form) return;
  const button = Array.from(form.elements).find(
    (element): element is HTMLButtonElement | HTMLInputElement =>
      (element instanceof HTMLButtonElement || element instanceof HTMLInputElement) &&
      element.type === 'submit',
  );
  if (button && !button.disabled) button.click();
}

/** The segments with one segment emptied. */
function without(values: SegmentValues, type: SegmentType): SegmentValues {
  const { [type]: _cleared, ...rest } = values;
  return rest;
}

function focusElement(element: HTMLElement | null | undefined) {
  element?.focus();
}

/**
 * The shared shell: an `Input`-looking group of spinbutton segments with a trailing icon button
 * that opens the picker in a popover anchored to the field. Each picker supplies the panel.
 */
export function TemporalField({
  kind,
  minuteStep,
  triggerIcon,
  triggerLabel,
  contentClassName,
  renderPanel,
  value,
  defaultValue,
  onChange,
  onBlur,
  name,
  disabled = false,
  required = false,
  readOnly = false,
  min: _min,
  max: _max,
  ref,
  locale: localeTag,
  dateOrder,
  timeZone,
  id,
  className,
  onKeyDown,
  onMouseDown,
  ...rest
}: TemporalFieldProps) {
  const { t } = useTranslation('common');
  const locale = useTemporalLocale(localeTag, dateOrder);
  const options: SegmentOptions = { hour12: locale.hour12, minuteStep };
  const state = useSegmentState({ kind, value, defaultValue, onChange, options });
  const { segments, update } = state;
  const layout = locale.layouts[kind];
  const types = layout.flatMap((item) => (item.kind === 'segment' ? [item.type] : []));
  /** Each layout item with its segment's position among the segments (-1 for a literal). */
  const items = layout.map((item, position) => ({
    item,
    position,
    index: item.kind === 'segment' ? types.indexOf(item.type) : -1,
  }));

  const groupRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const segmentRefs = useRef<Array<HTMLElement | null>>([]);
  const escapeRef = useRef<(() => boolean) | null>(null);
  const initialFocusRef = useRef<(() => void) | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [openSegments, setOpenSegments] = useState<SegmentValues>({});
  const [buffer, setBuffer] = useState<{ index: number; text: string } | null>(null);

  const interactive = !disabled && !readOnly;
  const empty = types.every((type) => segments[type] === undefined) && !buffer;

  const focusSegment = (index: number) => focusElement(segmentRefs.current[index]);
  const focusFirst = () => {
    const firstEmpty = types.findIndex((type) => segments[type] === undefined);
    focusSegment(firstEmpty === -1 ? 0 : firstEmpty);
  };
  const labelledBy = useLabelFor(id, groupRef, () => {
    if (!disabled) focusSegment(0);
  });

  // react-hook-form focuses the field through this ref (setFocus, focus on the first error).
  const setFirstSegment = (index: number) => (element: HTMLElement | null) => {
    segmentRefs.current[index] = element;
    if (index !== 0 || !ref) return;
    if (typeof ref === 'function') ref(element);
    else ref.current = element;
  };

  const openPicker = (returnTo: HTMLElement | null) => {
    if (!interactive) return;
    returnFocusRef.current = returnTo;
    setOpenSegments(segments);
    setBuffer(null);
    setOpen(true);
  };

  const close = () => setOpen(false);

  const onSegmentKeyDown = (index: number, type: SegmentType) => (event: React.KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey) return;
    const { key } = event;
    if ((event.altKey && key === 'ArrowDown') || key === 'F4') {
      event.preventDefault();
      openPicker(event.currentTarget as HTMLElement);
      return;
    }
    if (event.altKey) return;
    switch (key) {
      case 'ArrowLeft':
        event.preventDefault();
        if (index > 0) focusSegment(index - 1);
        return;
      case 'ArrowRight':
        event.preventDefault();
        if (index < types.length - 1) focusSegment(index + 1);
        return;
      case 'Enter':
        event.preventDefault();
        submitImplicitly(event.currentTarget as HTMLElement);
        return;
      case 'Tab':
        return;
    }
    if (readOnly) return;
    if (key === 'ArrowUp' || key === 'ArrowDown') {
      event.preventDefault();
      setBuffer(null);
      const year = nowIn(timeZone).date.year;
      const next = stepSegment(type, segments, key === 'ArrowUp' ? 1 : -1, options, year);
      update({ ...segments, [type]: next });
      return;
    }
    if (key === 'Backspace' || key === 'Delete') {
      event.preventDefault();
      setBuffer(null);
      if (segments[type] === undefined) return;
      update(without(segments, type));
      return;
    }
    if (key.length !== 1) return;
    event.preventDefault();
    if (/^\d$/.test(key) && type !== 'period') {
      const previous = buffer?.index === index ? buffer.text : '';
      const typed = typeDigit(previous, Number(key), typingLimits(type, options));
      update(
        typed.value === undefined ? without(segments, type) : { ...segments, [type]: typed.value },
      );
      if (typed.advance && index < types.length - 1) {
        setBuffer(null);
        focusSegment(index + 1);
      } else {
        setBuffer(typed.advance ? null : { index, text: typed.buffer });
      }
      return;
    }
    if (type === 'period') {
      const letter = key.toLowerCase();
      const match = locale.periodNames.findIndex((label, i) =>
        [label.trim().charAt(0).toLowerCase(), i === 0 ? 'a' : 'p'].includes(letter),
      );
      if (match !== -1) {
        update({ ...segments, period: match });
        if (index < types.length - 1) focusSegment(index + 1);
        return;
      }
    }
    // The literal after the segment (or any usual separator) moves on, as in Chrome.
    const position = items.find((entry) => entry.index === index)?.position ?? -1;
    const following = layout[position + 1];
    const literal = following?.kind === 'literal' ? following.text : '';
    if (
      (literal.includes(key) || SEPARATORS.includes(key)) &&
      (segments[type] !== undefined || buffer?.index === index) &&
      index < types.length - 1
    ) {
      setBuffer(null);
      focusSegment(index + 1);
    }
  };

  const segmentLabel = (type: SegmentType) => t(`dateTime.segment.${type}`);

  const valueText = (type: SegmentType, segmentValue: number | undefined) => {
    if (segmentValue === undefined) return t('dateTime.empty');
    if (type === 'month') return locale.monthNames[segmentValue - 1] ?? String(segmentValue);
    return segmentDisplay(type, segmentValue, locale);
  };

  const ariaInvalid = rest['aria-invalid'];
  const describedBy = rest['aria-describedby'];
  // The picker never stays open over a field that became disabled or read-only.
  const isOpen = open && interactive;

  return (
    <Popover
      open={isOpen}
      onOpenChange={(next) => (next ? openPicker(triggerRef.current) : close())}
      modal
    >
      <PopoverAnchor asChild>
        {/* The group hands presses on its padding to a segment, as a native field does. */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <div
          {...rest}
          ref={groupRef}
          id={id}
          role="group"
          aria-labelledby={rest['aria-labelledby'] ?? (rest['aria-label'] ? undefined : labelledBy)}
          aria-disabled={disabled || undefined}
          data-slot="temporal-input"
          data-disabled={disabled ? '' : undefined}
          data-empty={empty ? '' : undefined}
          className={cn(
            'border-input dark:bg-input/30 flex h-9 w-full min-w-0 items-center gap-1 rounded-md border bg-transparent pr-1 pl-3 text-base shadow-xs transition-[color,box-shadow] outline-none md:text-sm',
            'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
            'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
            'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
            className,
          )}
          onKeyDown={onKeyDown}
          onMouseDown={(event) => {
            onMouseDown?.(event);
            // A press on the padding or a literal focuses a segment, as a native field does.
            if (
              event.defaultPrevented ||
              disabled ||
              (event.target as HTMLElement).closest('[role="spinbutton"], button')
            ) {
              return;
            }
            event.preventDefault();
            focusFirst();
          }}
          onFocus={(event) => {
            const index = segmentRefs.current.indexOf(event.target);
            if (buffer && buffer.index !== index) setBuffer(null);
          }}
          onBlur={(event) => {
            const next = event.relatedTarget as Node | null;
            if (groupRef.current?.contains(next) || contentRef.current?.contains(next)) return;
            setBuffer(null);
            if (!isOpen) onBlur?.();
          }}
        >
          <span className="flex min-w-0 flex-1 items-center overflow-x-auto whitespace-nowrap tabular-nums [scrollbar-width:none]">
            {items.map(({ item, position, index }) => {
              if (item.kind === 'literal') {
                return (
                  <span
                    key={`literal-${position}`}
                    aria-hidden
                    className={cn('whitespace-pre', empty && 'text-muted-foreground')}
                  >
                    {item.text}
                  </span>
                );
              }
              const { type } = item;
              const segmentValue = segments[type];
              const pending = buffer?.index === index && segmentValue === undefined;
              const limits = typingLimits(type, options);
              const text =
                segmentValue !== undefined
                  ? segmentDisplay(type, segmentValue, locale)
                  : pending
                    ? buffer.text.padStart(2, '0')
                    : t(`dateTime.placeholder.${type}`);
              return (
                <span
                  key={type}
                  ref={setFirstSegment(index)}
                  role="spinbutton"
                  tabIndex={disabled ? -1 : 0}
                  aria-label={segmentLabel(type)}
                  aria-valuemin={limits.min}
                  aria-valuemax={limits.max}
                  aria-valuenow={segmentValue}
                  aria-valuetext={valueText(type, segmentValue)}
                  aria-invalid={ariaInvalid}
                  aria-describedby={describedBy}
                  aria-required={required || undefined}
                  aria-readonly={readOnly || undefined}
                  aria-disabled={disabled || undefined}
                  data-segment={type}
                  data-placeholder={segmentValue === undefined && !pending ? '' : undefined}
                  className={cn(
                    'rounded-xs px-px caret-transparent outline-none select-none',
                    'focus:bg-primary focus:text-primary-foreground',
                    'data-[placeholder]:not-focus:text-muted-foreground',
                  )}
                  onKeyDown={interactive || readOnly ? onSegmentKeyDown(index, type) : undefined}
                  onPointerDown={(event) => {
                    // Touch keyboards cannot type into segments; a tap opens the picker instead.
                    if (event.pointerType === 'touch' && interactive) {
                      event.preventDefault();
                      openPicker(event.currentTarget);
                    }
                  }}
                >
                  {text}
                </span>
              );
            })}
          </span>
          {name ? (
            <input type="hidden" name={name} value={composeValue(kind, segments, options)} />
          ) : null}
          <PopoverTrigger asChild>
            <Button
              ref={triggerRef}
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={triggerLabel}
              disabled={!interactive}
              className="text-muted-foreground hover:text-foreground shrink-0"
            >
              {triggerIcon}
            </Button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        ref={contentRef}
        align="start"
        aria-label={triggerLabel}
        className={cn('w-auto max-w-[calc(100vw-1rem)] p-0', contentClassName)}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          initialFocusRef.current?.();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          focusElement(returnFocusRef.current ?? triggerRef.current);
        }}
        onEscapeKeyDown={(event) => {
          if (escapeRef.current?.()) {
            event.preventDefault();
            return;
          }
          // Esc leaves the value as it was when the picker opened.
          update(openSegments);
        }}
      >
        {isOpen ? (
          <PanelSlot
            render={renderPanel}
            api={{
              segments,
              options,
              locale,
              update,
              close,
              escapeRef,
              initialFocusRef,
              timeZone,
            }}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/** Renders the picker's panel as its own component, so its hooks and refs live there. */
function PanelSlot({
  render,
  api,
}: {
  render: (api: TemporalPanelApi) => ReactNode;
  api: TemporalPanelApi;
}) {
  return render(api);
}
